<template>
  <div class="container mt-4" v-if="announcement">
    <TopNav>
      <div class="d-flex">
        <NavItem
          v-if="announcement"
          icon="far fa-save"
          :text="$t('MARKETING.LINKS.SAVE')"
          :pulse="isDirty"
          :loading="isLoading"
          @click.native="saveAnnouncement"
        />
      </div>
    </TopNav>
    <ValidationObserver ref="form">
      <div class="row">
        <div class="col-12">
          <h1>{{ $t('MARKETING.TITLES.ADD_ANNOUNCEMENT') }}</h1>
          <p>
            {{ $t('MARKETING.TEXT.ADD_AN_ANNOUNCEMENT_CARD_TO') }}
          </p>
        </div>
        <div class="col-12">
          <hr class="my-4" />
        </div>
        <div class="col-12 d-flex justify-content-between align-items-center">
          <label class="m-0 p-0">
            {{ $t('MARKETING.LABELS.ENABLE_ANNOUNCEMENT') }}
          </label>
          <HMSwitch v-model="announcement.enabled"></HMSwitch>
        </div>
        <div class="col-12">
          <hr class="my-4" />
        </div>
        <div class="col-12">
          <HMTextInput
            name="text"
            v-model="announcement.text"
            :label="$t('MARKETING.LABELS.ANNOUNCEMENT_TEXT')"
          ></HMTextInput>
        </div>
        <div class="col-12 d-flex justify-content-between align-items-center">
          <HMTextInput
            name="link"
            v-model="announcement.link"
            class="mr-4 flex-1"
            type="url"
            :label="$t('MARKETING.LABELS.CLICK_THROUGH_URL_OPTIONAL')"
          ></HMTextInput>
          <HMCheckBox v-model="announcement.open_in_new_tab">
            {{ $t('MARKETING.LABELS.OPEN_IN_A_NEW_TAB') }}
          </HMCheckBox>
        </div>
        <div class="col-12 d-flex justify-content-between align-items-center">
          <HMCheckBox :value="!!announcement.expire_at" @input="toggleExpireAt">
            {{ $t('MARKETING.LABELS.DISABLE_AT_A_SPECIFIC_TIME') }}
          </HMCheckBox>
          <HMDateSelect
            v-if="announcement.expire_at"
            :value="expireAtDate"
            @input="setExpireAtDate"
            :showTime="true"
            :minDate="minimumDate"
          >
            <span class="c-brand-color" role="button">
              {{ $formatDate(announcement.expire_at) }}
            </span>
          </HMDateSelect>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { errorToMessage } from '@/core/utils/errors';
import DirtyMixin from '../../../mixins/DirtyMixin';
import { HMCheckBox, HMTextInput } from '@/web/components/common';
import moment from 'moment';
import { ValidationObserver } from 'vee-validate';

function getMinimumDate() {
  return moment().subtract(1, 'day').toDate();
}

export default {
  name: 'AnnouncementDetail',
  components: {
    TopNav,
    NavItem,
    HMTextInput,
    HMCheckBox,
    ValidationObserver,
  },
  mixins: [DirtyMixin],
  data() {
    return {
      isLoading: false,
      announcement: undefined,
      minimumDate: getMinimumDate(),
    };
  },
  computed: {
    expireAtDate() {
      return {
        startDate: this.announcement.expire_at,
      };
    },
  },
  methods: {
    async getAnnouncement() {
      try {
        this.announcement = await this.$store.dispatch(
          'user/getTeacherAnnouncement'
        );
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.announcement = {
            enabled: false,
            text: '',
            link: '',
            open_in_new_tab: false,
            expire_at: null,
          };
        } else {
          this.$notify({
            group: 'app',
            type: 'error',
            title: this.$t(
              'MARKETING.NOTIFICATIONS.FAILED_TO_LOAD_ANNOUNCEMENT'
            ),
            text: errorToMessage(error?.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        }
      }
    },
    async saveAnnouncement() {
      try {
        await this.$store.dispatch(
          'user/updateTeacherAnnouncement',
          this.announcement
        );
        this.$notify({
          group: 'app',
          type: 'success',
          text: this.$t(
            'MARKETING.NOTIFICATIONS.ANNOUNCEMENT_SAVED_SUCCESSFULLY'
          ),
          duration: 3000,
        });
      } catch (error) {
        if (error?.response?.status === 400) {
          this.$refs.form.setErrors(error?.response?.data);
        } else {
          this.$notify({
            group: 'app',
            type: 'error',
            title: this.$t(
              'MARKETING.NOTIFICATIONS.FAILED_TO_SAVE_ANNOUNCEMENT'
            ),
            text: errorToMessage(error?.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        }
      }
    },
    toggleExpireAt() {
      if (this.announcement.expire_at) {
        this.announcement.expire_at = null;
      } else {
        this.announcement.expire_at = moment(getMinimumDate()).toISOString();
      }
    },
    setExpireAtDate(value) {
      this.announcement.expire_at = moment(value.startDate).toISOString();
    },
  },
  async mounted() {
    await this.getAnnouncement();
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
</style>
